import { BehaviorSubject, combineLatest, firstValueFrom, NEVER, Observable, ReplaySubject, Subscription, timeout } from 'rxjs';
import { effect, Injectable, NgZone, Optional, signal } from '@angular/core';
import { Auth, authState, signInWithCustomToken, signOut, User } from '@angular/fire/auth';
import { deleteDoc, doc, docData, Firestore, serverTimestamp, setDoc, updateDoc, writeBatch,docSnapshots } from '@angular/fire/firestore';
import { map, switchMap } from 'rxjs/operators';
import { ToastService } from 'src/app/services/toast/toast.service';
import { Router } from '@angular/router';
import { deleteToken, getToken, Messaging, onMessage } from '@angular/fire/messaging';
import { environment } from 'src/environments/environment';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { PhoneVerification, PhoneVerificationRequest } from 'src/models/phone-verification.model';

export interface UserData {
  phone: string,
  name: string,
  isMaster: boolean
}

export interface AdminData {
  phone: string,
  name: string,
  master: string,
  super: boolean
}

const SCOPE = '/';
const PUSH_TOKEN = 'pushToken';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(
    public auth: Auth,
    private firestore: Firestore,
    private functions: Functions,
    @Optional() private msg: Messaging | null,
    private ngZone: NgZone,
    private toast: ToastService,
    private router: Router,
  ) {
    authState(this.auth).pipe(
      switchMap(user => {
        this.user = user;
        if (!user) {
          this.authObs.next(false);
          return NEVER;
        }

        return combineLatest([
          docData(doc(this.firestore, 'users/' + user.uid).withConverter<UserData>({
            toFirestore(modelObject) {
              return modelObject;
            },
            fromFirestore(snapshot) {
              return <UserData>snapshot.data();
            }
          })),
          docData(doc(this.firestore, 'admins/' + user.phoneNumber!).withConverter<AdminData>({
            toFirestore(modelObject) {
              return modelObject;
            },
            fromFirestore(snapshot) {
              return <AdminData>snapshot.data();
            }
          })),
        ]);
      })
    ).subscribe(([userData, adminData]) => {
      if (!userData) {
        setDoc(doc(this.firestore, 'users/' + this.user!.uid), <UserData>{phone: this.user!.phoneNumber, name: this.localName ?? ''});
        return;
      }
      this.userData = userData;
      this.localName = this.userData.name;
      this.localPhone = this.userData.phone;
      this.userDataObs.next(userData);

      this.adminData = adminData;
      this.adminData$.next(adminData ?? null);
    });

    effect(() => {
      const token = this.pushToken$$();
      if(token) {
        localStorage.setItem(PUSH_TOKEN, token);
      } else {
        localStorage.removeItem(PUSH_TOKEN);
      }
    });

    if (this.pushIsSupported && this.msg) {
      onMessage(this.msg, (msg) => {
        this.toast.logWithButton(
          `${msg.notification?.title}. ${msg.notification?.body}`,
          'Открыть',
          () => this.ngZone.run(() => {
            if (this.isAdmin) {
              if (msg.data?.reserveId) {
                this.router.navigate(['', 'admin', 'reserve', msg.data.reserveId]);
              } else {
                this.router.navigate(['', 'tabs', 'tab-admin']);
              }
            } else {
              this.router.navigate(['', 'tabs', 'profile']);
            }
          })
        );
      });
      this._initSwRegistration(this.msg);
    }
  }

  public user: User | null = null;
  public userData?: UserData;
  private userDataSubs?: Subscription;
  public adminData?: AdminData;
  private adminDataSubs?: Subscription;
  public authObs = new BehaviorSubject<boolean>(false);
  public userDataObs = new BehaviorSubject<UserData | null>(null);
  public adminData$ = new ReplaySubject<AdminData | null>(1);
  public pushObs = new BehaviorSubject<string | undefined>(undefined);
  // public token?: string;
  public localPhone: string = '+7';
  public localName?: string;

  readonly pushIsSupported = 'Notification' in window && 'serviceWorker' in navigator;
  readonly registration$ = new ReplaySubject<ServiceWorkerRegistration>(1);
  readonly pushEnabled$ = new BehaviorSubject<boolean>(!!localStorage.getItem(PUSH_TOKEN));
  readonly pushToken$$ = signal<string | null>(localStorage.getItem(PUSH_TOKEN));

  phoneRequest$(id: string):Observable<PhoneVerification|undefined> {
    return docSnapshots(doc(this.firestore, 'phoneRequests', id)).pipe(map(doc => doc.exists() ? ({
      ...doc.data() as PhoneVerification,
      id: doc.id
    }) : undefined));
  }

  async verifyPhone(clientPhoneNumber: string) {
    const callable = httpsCallable<PhoneVerificationRequest, PhoneVerification>(this.functions, 'confirmPhone');
    const result = await callable({ clientPhoneNumber, });
    return result.data;
  }

  signInWithToken(token:string) {
    return signInWithCustomToken(this.auth,token);
  }


  public get isAdmin(): boolean {
    return !!this.adminData;
  }

  public get isSuperAdmin(): boolean {
    return !!(this.adminData && this.adminData.super);
  }

  async updateName(name: string): Promise<void> {
    this.localName = name;
    const uid = this.user?.uid;
    if (!uid) {
      return;
    }
    return updateDoc(doc(this.firestore, 'users', uid), {name: name});
  }

  async logout(): Promise<void> {
    await signOut(this.auth);
    this.adminData = undefined;
    this.userData = undefined;
    this.localPhone = '+7';
    this.localName = undefined;
    this.adminData$.next(null);
    this.userDataObs.next(null);
    this.user = null;
    if (this.userDataSubs) {
      this.userDataSubs.unsubscribe();
    }
    if (this.adminDataSubs) {
      this.adminDataSubs.unsubscribe();
    }
    if (this.pushToken$$()) {
      await this.unsubscribeFromPush(true);
    }
  }


  // Web Push

  async subscribeToPush() {
    if (!this.pushIsSupported || !this.msg) {
      await this.toast.log('Уведомления не поддерживаются вашим браузером');
      return;
    }
    const result = await Notification.requestPermission();
    if (result != 'granted') {
      await this.toast.log('Разрешите уведомления в настройках браузера');
      return;
    }
    const serviceWorkerRegistration = await firstValueFrom(this.registration$.pipe(timeout(3000))).catch(e=>undefined);
    if(!serviceWorkerRegistration) {
      await this.toast.log('Ошибка. Код 215.');
      return;
    }

    let token: string | null = null;
    const getMessagingToken = () => getToken(this.msg!, {
      vapidKey: environment.vapidKey,
      serviceWorkerRegistration,
    });

    try {
      token = await getMessagingToken();
    } catch (error: any) {
      const isInstallations404 =
        typeof error?.message === 'string' &&
        error.message.includes('Installations') &&
        error.message.includes('Requested entity was not found');
      if (isInstallations404) {
        try {
          await deleteToken(this.msg);
          token = await getMessagingToken();
        } catch (retryError) {
          console.error('FCM token recovery failed', retryError);
          await this.toast.log('Не удалось восстановить токен уведомлений. Попробуйте позже.');
          return;
        }
      } else {
        console.error('FCM token request failed', error);
        await this.toast.log('Ошибка при включении уведомлений. Попробуйте позже.');
        return;
      }
    }

    if (!token) {
      await this.toast.log('Ошибка при включении уведомлений. Код 216.');
      return;
    }

    await setDoc(
      doc(this.firestore, 'devices', token),
      this._getTokenDocData(token),
    );
    this.pushEnabled$.next(true);
    this.pushToken$$.set(token);
    await this.toast.log('Уведомления включены');
  }

  private _getTokenDocData(token: string) {
    return {
      user: this.user?.uid ?? null,
      phone: this.userData?.phone ?? this.localPhone,
      token,
      master: this.adminData?.master ?? null,
      created: serverTimestamp(),
      web: true,
    };
  }

  async unsubscribeFromPush(silent: boolean = false) {
    if (!this.msg) {
      return;
    }
    try {
      await deleteToken(this.msg);
    } catch (e) {
      console.error('delete token error');
    }

    const token = this.pushToken$$();
    if (token) {
      await deleteDoc(doc(this.firestore, 'devices', token));
      this.pushToken$$.set(null);
    }
    this.pushEnabled$.next(false);

    if (!silent) {
      await this.toast.log('Уведомления отключены');
    }
  }




  private async _initSwRegistration(msg:Messaging) {
    if (!('serviceWorker' in navigator)) {
      return;
    }
    const regs = await navigator.serviceWorker.getRegistrations();
    // Найти дефолтный воркер и удалить (это фикс бага, потом можно удалить)
    const defaultReg = regs.find(r => r.scope == location.origin + '/firebase-cloud-messaging-push-scope');
    if(defaultReg) {
      try {
        await defaultReg.unregister();
      } catch (e) {

      }
    }
    const desiredScope = new URL(SCOPE, location.origin).href;
    let reg = regs.find(r => r.scope === desiredScope);
    if (reg) {
      await reg.update();
    } else {
      reg = await navigator.serviceWorker
        .register('firebase-messaging-sw.js', {
          type: 'module',
          scope: SCOPE,
        });
    }
    this.registration$.next(reg);
    const existToken = this.pushToken$$();
    // Если токен есть, то запрашиваем снова для того, чтобы сохранить vapidKey и serviceWorkerRegistration в Messaging, т.к. он используется в вызовах httpsCallable
    if (existToken) {
      let token: string | null = null;
      try {
        token = await getToken(msg,{vapidKey:environment.vapidKey,serviceWorkerRegistration:reg});
      } catch (error) {
        console.error('FCM token refresh failed', error);
        await this.toast.log('Не удалось обновить токен уведомлений. Попробуйте включить уведомления снова.');
        this.pushEnabled$.next(false);
        this.pushToken$$.set(null);
        return;
      }
      if(!token || token==this.pushToken$$()) {
        // Токен не изменился
        return;
      }
      // Токен изменился, удаляем старый и сохраняем новый
      const batch = writeBatch(this.firestore);
      batch.set(doc(this.firestore, 'devices', token),this._getTokenDocData(token));
      batch.delete(doc(this.firestore, 'devices', existToken));
      await batch.commit();
      this.pushToken$$.set(token);
    }
  }
}
